import { createRouter, createWebHistory } from '@ionic/vue-router';
import { NavigationGuardNext, RouteLocationNamedRaw, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { Capacitor } from "@capacitor/core";

import { ROUTE_NAME, routes as appRoutes } from './appRoutes';
import { ROUTE_NAME_WEB, routes as webRoutes } from './webRoutes';

import { usePersistentStore } from "@/stores/persistent";
import { useLocationStore } from "@/stores/locations";

import { getPreference } from "@/functions/preferences";
import { forceAppVersion, isApp, setForceAppVersion } from "@/functions/enviroment";

import { useApi } from "@/composables/useApi";
import { useNotificationStore } from "@/stores/notification";
import { modalController } from "@ionic/vue";

const getRoutes = (): RouteRecordRaw[] => {
    if (isApp()) return appRoutes

    return webRoutes
}

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes: getRoutes(),
})

const removeRoutes = () => {
    const routes = router.getRoutes()
    for (const route of routes) {
        if (!route.name) continue
        router.removeRoute(route.name)
    }
}

const addAppRoutes = () => {
    const routes = getRoutes()
    for (const route of routes) {
        if (!route.name) continue
        router.addRoute(route)
    }
}

export const changeToAppRoutes = () => {
    setForceAppVersion(true)
    removeRoutes()
    addAppRoutes()
    router.push({ name: ROUTE_NAME.START })
}

const route = router.currentRoute

const goRoot = () => {
    router.push({ name: ROUTE_NAME.START })
}

interface RoutingCopyVariable {
    params?: string | string[] | 'all';
    query?: string | string[] | 'all'
}

type Params = Record<string, string | string[]>
type Query = Record<string, (string | null) | (string | null)[]>

const copyRouteVariables = (copy: RoutingCopyVariable): { params: Params | undefined; query: Query | undefined } => {
    let params: Params | undefined;
    let queries: Query | undefined;

    if (copy.params) {
        const routerParams = route.value.params

        if (typeof copy.params === 'string') {
            if (copy.params === 'all') {
                params = routerParams
            } else {
                params = { [copy.params]: routerParams[copy.params] }
            }
        } else {
            params = {}

            for (const param of copy.params) {
                params[param] = routerParams[param]
            }
        }
    }

    if (copy.query) {
        const routerQueries = route.value.query

        if (typeof copy.query === 'string') {
            if (copy.query === 'all') {
                queries = routerQueries
            } else {
                queries = { [copy.query]: routerQueries[copy.query] }
            }
        } else {
            queries = {}

            for (const query of copy.query) {
                queries[query] = routerQueries[query]
            }
        }
    }

    return {
        params: params,
        query: queries
    }
}

const routerPush = async (to: RouteLocationNamedRaw, copy?: RoutingCopyVariable) => {
    if (copy) {
        const copiedValues = copyRouteVariables(copy)
        if (copiedValues.params && to.params) {
            const toParamsKeys = Object.keys(to.params)
            for (const paramKey of Object.keys(copiedValues.params)) {
                if (toParamsKeys.includes(paramKey)) delete copiedValues.params[paramKey]
            }
        }
        to.params = { ...to.params, ...copiedValues.params }
        to.query = { ...to.query, ...copiedValues.query }
    }

    await router.push(to)
}

const routeGuardApp = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> => {
    if (forceAppVersion) {
        const top = await modalController.getTop()

        if (top) {
            modalController.dismiss()
            return next(false)
        }
    }

    const user = await getPreference("userStore")
    const persistentStore = usePersistentStore()
    let sessionToken = undefined

    if (to.meta.force) return next()

    if (user) {
        sessionToken = JSON.parse(user)?.sessionInfo?.sessionToken
    }

    if (sessionToken && persistentStore.offlineMode && persistentStore.offlineModeRoute && to.fullPath !== persistentStore.offlineModeRoute)
        return next({ path: persistentStore.offlineModeRoute })

    if (!to.meta?.noAuth && !sessionToken) return next({ name: ROUTE_NAME.LOGIN })

    if (to.meta?.noAuth && sessionToken) {
        const locationStore = useLocationStore()
        if (to.name === ROUTE_NAME_WEB.HOME && locationStore.activeLocation) locationStore.activeLocation = null

        if (locationStore.activeLocation) return next({
            name: ROUTE_NAME.DASHBOARD_HOME,
            params: {
                locationId: locationStore.activeLocation.id
            }
        })

        return next({ name: ROUTE_NAME.HOME })
    }

    next()
}

const locationRerouting = async (to: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> => {
    const locationStore = useLocationStore()
    if (locationStore.activeLocation) return next({
        name: ROUTE_NAME.DASHBOARD_HOME,
        params: {
            locationId: locationStore.activeLocation.id
        }
    })

    if (to.name === ROUTE_NAME_WEB.HOME && locationStore.activeLocation) locationStore.activeLocation = null

    return next({ name: ROUTE_NAME_WEB.HOME })
}

const activeLocationCheck = (to: RouteLocationNormalized): boolean => {
    const locationStore = useLocationStore()
    return !!to.params.locationId && (!locationStore.activeLocation || locationStore.activeLocation.id !== to.params.locationId)
}

const routeGuardWeb = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> => {
    const locationStore = useLocationStore()
    const user = await getPreference("userStore")

    if (to.meta?.force) return next()

    let sessionToken = undefined

    if (activeLocationCheck(to)) {
        locationStore.forcedLocationChange(String(to.params.locationId))
    }

    if (user) sessionToken = JSON.parse(user)?.sessionInfo?.sessionToken

    if (sessionToken) useNotificationStore().getNotifications()

    if (!to.meta?.noAuth && !sessionToken) return next({ name: ROUTE_NAME_WEB.LOGIN })

    if (to.meta?.noAuth && sessionToken) return locationRerouting(to, next)

    next()
}

router.beforeEach(async (to, from, next) => {
    if (Capacitor.isNativePlatform() || forceAppVersion) return routeGuardApp(to, from, next)
    await routeGuardWeb(to, from, next)
})

router.afterEach(() => {
    if (isApp()) return;
    const { testCount } = useApi()
    testCount.value++
})

export default router

export {
    route,
    ROUTE_NAME,
    goRoot,
    routerPush,
}
